import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useWindowSize } from "lib/hooks";
import ReactGA from 'react-ga';
import log from "loglevel";
import { Modal } from "react-bootstrap";
import Select, { ValueType } from "react-select";
// @ts-ignore
import BootstrapTable from "react-bootstrap-table-next";
// @ts-ignore
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
// @ts-ignore
import paginationFactory from "react-bootstrap-table2-paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addUserAction,
  modifyUserAction,
  deleteUserAction
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  validUsername,
  validEmail,
  validPassword,
  validFirstName,
  validLastName,
  isStringValid,
  getInputValidity
} from "util/formValidators";
import { options } from "util/reactTableFuncs";
import { generateRoleDropdownItems } from "util/dashboardUtil";
import {
  User,
  Role,
  CreateUserRequestType,
  ModifyUserRequestType,
  DeleteUserRequestType,
  RoleDropdownItemType
} from "types";
import { parseTimestamp, xIfFalseFormatter } from "util/reactTableFuncs";
import genStyles from "styles/General.module.scss";
import styles from "components/dashboard/Dashboard.module.scss";
import { Table, Form } from "@avlino-inc/alenza-react";
import { administratorRoleId } from "settings";
import { isUndefined } from "util";

type InputProps = {
	property: "username" | "email" | "first_name" | "last_name", 
	placeholder:string, 
	validFormat: boolean |undefined 
}

type InputModifyProps = {
	property: "username" | "email" | "first_name" | "last_name" | "password", 
	placeholder: string, 
	validFormat: boolean |undefined 
}

type InputComponent = React.FC<InputProps>
type InputModifyComponent = React.FC<InputModifyProps>

type CreateUserFormType = {
  username: string | undefined;
  email: string | undefined;
  first_name: string | undefined;
  last_name: string | undefined;
  require_twofactor: boolean;
  role_id: number | undefined;
};

const defaultUser = {
  username: undefined,
  email: undefined,
  first_name: undefined,
  last_name: undefined,
  require_twofactor: false,
  role_id: undefined
};

type ModifyUserFormType = {
  username: string | undefined;
  email: string | undefined;
  password: string | undefined;
  first_name: string | undefined;
  last_name: string | undefined;
  require_twofactor: boolean;
  role_id: number | undefined;
};

const defaultModifyUser = {
  ...defaultUser,
  password: undefined
};

const UsersCard = ({
  users,
  roles,
  compactView,
  showSearch,
  userIsAdmin,
  userIsAccountOwner,
  showCreateModal,
  setShowCreateModal
}: {
  users: User[];
  roles: Role[];
  compactView: boolean;
  showSearch: boolean;
  userIsAdmin: boolean;
  userIsAccountOwner: boolean;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const dispatch = useDispatch();
  const windowSize = useWindowSize();

  const [selectedUser, setSelectedUser] = useState<User>();
  const [
    selectedUserRoleDropdownItem,
    setSelectedUserRoleDropdownItem
  ] = useState<ValueType<RoleDropdownItemType, false>>();
  const [createUser, setCreateUser] = useState<CreateUserFormType>(defaultUser);
  const [modifyUser, setModifyUser] = useState<ModifyUserFormType>(
    defaultModifyUser
  );
  const [deleteUserConfirm, setDeleteUserConfirm] = useState<string>();
  // eslint-disable-next-line
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  // eslint-disable-next-line
  const [isModifyingUser, setIsModifyingUser] = useState(false);
  // eslint-disable-next-line
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  // NOTE: Moved to outside component
  // const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const validCreateUserFields = (userForm: CreateUserFormType | undefined) => {
    return (
      userForm &&
      validUsername(userForm?.username) &&
      validEmail(userForm?.email) &&
      validFirstName(userForm?.first_name) &&
      validLastName(userForm?.last_name) &&
      userForm.require_twofactor !== undefined &&
      userForm.role_id !== undefined
    );
  };

  const validModifyUserFields = (userForm: ModifyUserFormType | undefined) => {
    return (
      userForm &&
      validUsername(userForm?.username) &&
      validEmail(userForm?.email) &&
      (validPassword(userForm?.password) || userForm?.password === undefined) && // allowed to be undefined
      validFirstName(userForm?.first_name) &&
      validLastName(userForm?.last_name)
    );
  };

  const onHandleCreateUserClick = (event: any) => {
    event.preventDefault();
    if (
      createUser?.username &&
      createUser?.email &&
      createUser?.first_name &&
      createUser?.last_name &&
      createUser?.require_twofactor !== undefined &&
      createUser?.role_id &&
      validCreateUserFields(createUser)
    ) {
      log.info("All fields are valid! Creating User...");
      const request: CreateUserRequestType = {
        body: {
          username: createUser.username,
          email: createUser.email,
          first_name: createUser.first_name,
          last_name: createUser.last_name,
          require_twofactor: createUser.require_twofactor,
          role_id: createUser.role_id
        }
      };
      log.info("-- Dispatching addUserAction()...");
      dispatch(addUserAction(request));
      ReactGA.event({
        category: 'Create User',
        action: `User: ${createUser.username}`
      });
      setIsCreatingUser(false);
      setShowCreateModal(false);
      setCreateUser(defaultUser); // clear
    } else {
      log.info("One or more fields NOT VALID! Will not create User.");
    }
  };

  const onHandleModifyUserClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to modify User...");
    if (
      selectedUser &&
      selectedUser.id &&
      modifyUser?.username &&
      modifyUser?.email &&
      modifyUser?.first_name &&
      modifyUser?.last_name &&
      modifyUser?.require_twofactor !== undefined &&
      modifyUser?.role_id &&
      validUsername(modifyUser?.username) &&
      validEmail(modifyUser?.email)
    ) {
      log.info("All fields valid! Modifying...");
      const request: ModifyUserRequestType = {
        id: selectedUser.id,
        body: {
          username: modifyUser.username,
          password: modifyUser.password ? modifyUser.password : null,
          email: modifyUser.email,
          first_name: modifyUser.first_name,
          last_name: modifyUser.last_name,
          require_twofactor: modifyUser.require_twofactor,
          role_id: modifyUser.role_id
        }
      };
      log.info("-- Dispatching modifyUserAction()...");
      dispatch(modifyUserAction(request));
      ReactGA.event({
        category: 'Modify User',
        action: `User: ${modifyUser.username}`
      });
      setIsModifyingUser(false);
      setShowSettingsModal(false);
      setSelectedUser(undefined); // clear
      setModifyUser(defaultModifyUser); // clear
    } else {
      log.info("One or more fields NOT VALID!");
      log.info(JSON.stringify(modifyUser));
    }
  };

  const confirmUserDelete = () => {
    return selectedUser && selectedUser.username === deleteUserConfirm;
  };

  const onHandleDeleteUserClick = (event: any) => {
    event.preventDefault();
    if (selectedUser && confirmUserDelete()) {
      const request: DeleteUserRequestType = {
        id: selectedUser.id
      };
      log.info("-- Dispatching deleteUserAction()...");
      dispatch(deleteUserAction(request));
      ReactGA.event({
        category: 'Delete User',
        action: `User ID: ${selectedUser.id}`
      });
      setIsDeletingUser(false);
      setShowSettingsModal(false);
      setSelectedUser(undefined); // clear
      setDeleteUserConfirm(""); //clear
    }
  };

  const onSelectRoleFromDropdown = (roleItem: any, formType: string) => {
    if (roleItem) {
      log.info(
        "Create User Role Dropdown Item Selected! : " + JSON.stringify(roleItem)
      );
      if (formType === "create") {
        setCreateUser((state): typeof state => ({
          ...state,
          role_id: roleItem.id
        }));
      } else if (formType === "modify") {
        setModifyUser((state): typeof state => ({
          ...state,
          role_id: roleItem.id
        }));
      }
    }
  };

  const usersTable = (users: User[], showSearch: boolean) => {
    const { SearchBar } = Search;
    const columns = [
      {
        dataField: "username",
        text: "Username",
        sort: true,
        headerAlign: "left",
        align: "left"
      }
    ];
    if (windowSize.width > 1400) {
      columns.push({
        dataField: "email",
        text: "Email",
        sort: true,
        headerAlign: "left",
        align: "left"
      });
    }
    if (compactView && windowSize.width > 1100) {
      columns.push({
        dataField: "first_name",
        text: "First Name",
        sort: false,
        headerAlign: "left",
        align: "left"
      });
      columns.push({
        dataField: "last_name",
        text: "Last Name",
        sort: true,
        headerAlign: "left",
        align: "left"
      });
      columns.push({
        dataField: "role_name",
        text: "Role",
        sort: true,
        headerAlign: "left",
        align: "left"
      });
      columns.push({
        dataField: "email_verified",
        text: "Verified",
        // @ts-ignore
        formatter: xIfFalseFormatter
      });
      columns.push({
        dataField: "require_twofactor",
        text: "Two-factor",
        // @ts-ignore
        formatter: xIfFalseFormatter
      });
      columns.push({
        dataField: "last_login",
        text: "Last Login",
        // @ts-ignore
        formatter: parseTimestamp,
        sort: true
      });
    }
    const rowEvents = {
      onClick: (e: any, row: User, rowIndex: number) => {
        log.info("Row Index: " + rowIndex);
        log.info("Row: " + JSON.stringify(row));
        setSelectedUser(row);
        setModifyUser({
          username: row.username,
          email: row.email,
          password: undefined,
          first_name: row.first_name,
          last_name: row.last_name,
          require_twofactor: row.require_twofactor,
          role_id: row.role_id
        });
        const matchingRole: Role | undefined = roles.find(
          role => role.id === row.role_id
        );
        if (matchingRole) {
          const item: ValueType<RoleDropdownItemType, false> = {
            id: row.role_id,
            label: matchingRole.name,
            role: matchingRole
          };
          setSelectedUserRoleDropdownItem(item);
        }
        setShowSettingsModal(true);
      }
    };
    const dataSorted = users.sort((a: User, b: User) =>
      a.username.localeCompare(b.username)
	);
	
    return (
      <>
        <ToolkitProvider
          keyField="id"
          data={dataSorted}
          columns={columns}
          search
        >
          {(props: any) => (
            <div>
              { showSearch ?
              <div className={styles["table__search-container"]}>
                <SearchBar
                  className={styles["table__search-container__search-bar"]}
                  {...props.searchProps}
                />
              </div>
              : null }
              <div className={Table.classNames.table}>
                <BootstrapTable
                  {...props.baseProps}
                  bordered={false}
                  bootstrap4
                  hover
                  condensed
                  rowEvents={rowEvents}
                  pagination={paginationFactory(options)}
                />
              </div>
            </div>
          )}
        </ToolkitProvider>
      </>
    );
  };
  

  return (
    <>
      {(userIsAdmin || userIsAccountOwner) && users && roles
        ? usersTable(users, showSearch)
        : [
            // Do not want to display restriction message until actions complete
            users === undefined || roles === undefined ? null : (
              <div className={styles["dashboard-card__restricted-text"]}>
                <p>
                  <i>
                    You do not have access to view global Users. Contact an
                    Administrator to grant access.
                  </i>
                </p>
              </div>
            )
          ]}
      {/* Create User Modal */}
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateUser(defaultUser);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Create User <FontAwesomeIcon icon={["fal", "user-plus"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-user-form">
            <Modal.Body>
              <div className="w-100" style={{ textAlign: "center" }}>
				  <Form.Group controlId={'create_user_username'}>
					<Form.Input
						type="text"
						placeholder={'Username'}
						status={getInputValidity(createUser?.username, validUsername)}
						onChange={(e: any) => {
							if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setCreateUser((state): typeof state => ({
								...state,
								username: value
							}));
							}
						}}
					/>
				  </Form.Group>

				  <Form.Group controlId={'create_user_email'}>
					<Form.Input
						type="text"
						placeholder={'Email'}
						status={getInputValidity(createUser?.email, validEmail)}
						onChange={(e: any) => {
							if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setCreateUser((state): typeof state => ({
								...state,
								email: value
							}));
							}
						}}
					/>
				  </Form.Group>

				  <Form.Group controlId={'create_user_first_name'}>
					<Form.Input
						type="text"
						placeholder={'First Name'}
						status={getInputValidity(createUser?.first_name, validFirstName)}
						onChange={(e: any) => {
							if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setCreateUser((state): typeof state => ({
								...state,
								first_name: value
							}));
							}
						}}
					/>
				  </Form.Group>
				  <Form.Group controlId={'create_user_last_name'}>
					<Form.Input
						type="text"
						placeholder={'Last Name'}
						status={getInputValidity(createUser?.last_name, validLastName)}
						onChange={(e: any) => {
							if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setCreateUser((state): typeof state => ({
								...state,
								last_name: value
							}));
							}
						}}
					/>
				  </Form.Group>
              </div>
			  {/**
			   * NOTE: Hidden to hide since from client view, since we do not impleement this ATM. Reenable if we start using two-factor auth again.
			   */}
              {/* <div style={{ marginTop: "15px", marginBottom: "15px" }}>
                <span>Require Two-factor:</span>
                <label className="switch" style={{ marginLeft: "10px" }}>
                  <input
                    type="checkbox"
                    defaultChecked={createUser.require_twofactor}
                    onClick={(e: any) => {
                      if (e.target instanceof HTMLInputElement) {
                        setCreateUser((state): typeof state => ({
                          ...state,
                          require_twofactor: createUser.require_twofactor
                            ? false
                            : true
                        }));
                      }
                    }}
                  />
                  <span className="slider round"></span>
                </label>
              </div> */}
              <Select
                options={generateRoleDropdownItems(
                  roles,
                  !userIsAdmin ? administratorRoleId : 0
                )}
                placeholder={"Select Role..."}
                isClearable={false}
                onChange={(e: ValueType<RoleDropdownItemType, false>) =>
                  onSelectRoleFromDropdown(e, "create")
                }
                isOptionDisabled={option => option.disabled === "yes"}
              />
              {!userIsAdmin && userIsAccountOwner ? (
                <p style={{ marginTop: "15px" }}>
                  <i>
                    The Administrator role selection has been disabled because
                    you are not an Administrator
                  </i>
                </p>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Create"
                className={[
                  genStyles["alenza__btn-create__mini"],
                  "float-right"
                ].join(" ")}
                disabled={!validCreateUserFields(createUser)}
                onClick={(e: any) => {
                  log.info("Create User Button Clicked!");
                  setIsCreatingUser(true);
                  onHandleCreateUserClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
      {/* User Settings Modal */}
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showSettingsModal}
          onHide={() => {
            setShowSettingsModal(false);
            setModifyUser(defaultModifyUser);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              User Settings <FontAwesomeIcon icon={["fal", "user-cog"]} />
            </Modal.Title>
          </Modal.Header>
          {userIsAdmin ? (
            <form name="modify-user-form">
              <Modal.Body>
                <h5>
                  Edit <FontAwesomeIcon icon={["fal", "user-edit"]} />
                </h5>
                <div className="w-100" style={{ textAlign: "center" }}>
				<Form.Group controlId={'modify_user_username'}>
				<Form.Input
					type="text"
					placeholder='Username'
					value={modifyUser?.username || ""}
					status={getInputValidity(modifyUser?.username, validUsername)}
					onChange={(e: any) => {
						if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setModifyUser((state): typeof state => ({
							...state,
							username: value
							}));
						}
						}}
				/>
				</Form.Group>
  
				<Form.Group controlId={'modify_user_email'}>
				<Form.Input
					type="text"
					placeholder='Email'
					value={modifyUser?.email || ""}
					status={getInputValidity(modifyUser?.email, validEmail)}
					onChange={(e: any) => {
						if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setModifyUser((state): typeof state => ({
							...state,
							email: value
							}));
						}
						}}
				/>
				</Form.Group>
  
				<Form.Group controlId={'modify_user_password'}>
				<Form.Input
					type="password"
					placeholder='Password (Leave blank to keep current)'
					value={modifyUser?.password || ""}
					status={getInputValidity(modifyUser?.password, validPassword)}
					onChange={(e: any) => {
						if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setModifyUser((state): typeof state => ({
								...state,
								password: value
							}));
						}
					}}
					autoComplete="new-password"
				/>
				</Form.Group>

				<Form.Group controlId={'modify_user_first_name'}>
				<Form.Input
					type="text"
					placeholder='First Name'
					value={modifyUser?.first_name || ""}
					status={getInputValidity(modifyUser?.first_name, validFirstName)}
					onChange={(e: any) => {
						if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setModifyUser((state): typeof state => ({
							...state,
							first_name: value
							}));
						}
						}}
				/>
				</Form.Group>

				<Form.Group controlId={'modify_user_last_name'}>
				<Form.Input
					type="text"
					placeholder='Last Name'
					value={modifyUser?.last_name || ""}
					status={getInputValidity(modifyUser?.last_name, validLastName)}
					onChange={(e: any) => {
						if (e.target instanceof HTMLInputElement) {
							const value = e.target.value;
							setModifyUser((state): typeof state => ({
							...state,
							last_name: value
							}));
						}
						}}
				/>
				</Form.Group>
		  
                </div>
				{/**
				 * NOTE: Hidden to hide since from client view, since we do not impleement this ATM. Reenable if we start using two-factor auth again.
				 */}
                {/* <div style={{ marginTop: "15px", marginBottom: "15px" }}>
                  <span>Require Two-factor: </span>
                  <label className="switch" style={{ marginLeft: "10px" }}>
                    <input
                      type="checkbox"
                      defaultChecked={modifyUser?.require_twofactor}
                      onClick={(e: any) => {
                        if (e.target instanceof HTMLInputElement) {
                          setModifyUser((state): typeof state => ({
                            ...state,
                            require_twofactor: modifyUser.require_twofactor
                              ? false
                              : true
                          }));
                        }
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                </div> */}
                <Select
                  options={generateRoleDropdownItems(roles)}
                  placeholder={"Select Role..."}
                  isClearable={false}
                  defaultValue={selectedUserRoleDropdownItem}
                  onChange={(value: ValueType<RoleDropdownItemType, false>) =>
                    onSelectRoleFromDropdown(value, "modify")
                  }
                />
                <div style={{ marginTop: "20px" }}>
                  <input
                    type="submit"
                    value="Save"
                    className={[
                      genStyles["alenza__btn-create__mini"],
                      "float-right"
                    ].join(" ")}
                    disabled={!validModifyUserFields(modifyUser)}
                    onClick={(e: any) => {
                      setIsModifyingUser(true);
                      onHandleModifyUserClick(e);
                    }}
                  />
                </div>
              </Modal.Body>
            </form>
          ) : (
            <Modal.Body>
              <p>
                <b>Username:</b> {selectedUser?.username}
              </p>
              <p>
                <b>Email:</b> {selectedUser?.email}
              </p>
              <p>
                <b>Name:</b>{" "}
                {`${selectedUser?.first_name} ${selectedUser?.last_name}`}
              </p>
            </Modal.Body>
          )}
          {userIsAdmin ? (
            <form name="delete-form" style={{ marginBottom: "15px" }}>
              <Modal.Body>
                <h5>
                  Delete <FontAwesomeIcon icon={["fal", "user-times"]} />
                </h5>
                <p>Please confirm the username to delete this User:</p>
                <div>
					<Form.Group controlId="delete_user_username">
					<Form.Input
						type="text"
						placeholder="Confirm Username"
						status={confirmUserDelete() ? 'valid' : 'invalid'}
						onChange={e => {
						  setDeleteUserConfirm(e.target.value);
						}}
					/>
					</Form.Group>
                </div>
                <p>Are you sure you want to delete this User?</p>
                <p className="font-bold">
                  This action is permanent and irreversible.
                </p>
                <div>
                  <input
                    type="submit"
                    value="Delete"
                    className={[
                      genStyles["alenza__btn-delete__mini"],
                      "float-right"
                    ].join(" ")}
                    disabled={!confirmUserDelete()}
                    onClick={(e: any) => {
                      setIsDeletingUser(true);
                      onHandleDeleteUserClick(e);
                    }}
                  />
                </div>
              </Modal.Body>
            </form>
          ) : null}
        </Modal>
      </>
    </>
  );
};

export default UsersCard;
